/**
 * Who is signed in, according to the API.
 *
 * The browser holds no token. Sign-in is a full-page navigation to
 * `/api/auth/login`, the API performs the exchange and sets an opaque session
 * cookie, and this asks `/api/auth/me` who that cookie belongs to.
 *
 * There is no SDK, no silent renewal and no token cache here: an expired
 * access token is refreshed by the API underneath the request, which is
 * invisible from this side and is the point of the arrangement.
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AUTH_BYPASS, BYPASS_USER } from "@/lib/auth-bypass";
import { ApiError, get, post } from "@/services/api-client";
import { clearAccessDenied } from "@/lib/access-denied";
import {
  clearSessionExpired,
  isSessionExpired,
  noteSignedIn,
  subscribeSessionExpired,
} from "@/lib/session-expiry";
import { useAnalytics } from "@/contexts/AnalyticsContext";

interface AuthUser {
  userId: string;
  email: string | undefined;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  /** The session this browser held has ended (a 401 after signing in), so the attorney is told before signing in again. */
  sessionEnded: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  /** Hand off to the API, returning to `returnTo` once signed in. */
  login: (returnTo?: string) => void;
  logout: () => void;
  /**
   * The API did not answer who is signed in (a 5xx, or no answer at all), so
   * nobody is known to be signed in or out. Not a sign-out: the session may be
   * fine, and only a 401 says it is gone.
   */
  unreachable: boolean;
  /** Ask the API again who is signed in. */
  retry: () => void;
}

const AuthValueContext = createContext<AuthContextValue | null>(null);

/** Where the API serves sign-in. Same origin, so the cookie rides along. */
const AUTH_BASE = "/api/auth";

/** What `/auth/me` answers with. */
interface Identity {
  user_id: string;
  email: string | null;
}

/** What `/auth/logout` answers with. */
interface SignedOut {
  logout_url: string;
}

/**
 * Who this browser's cookie names, asked of the API on mount and again on each
 * Try again; and whether the API answered at all.
 */
function useWhoIsSignedIn() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  // Each ask is one attempt; Try again starts the next.
  const [attempt, setAttempt] = useState(0);

  // One question on mount, and on each Try again: does this browser's cookie
  // name a live session? A 401 is the ordinary answer for a signed-out
  // visitor, not an error.
  useEffect(() => {
    // A controller rather than a flag: unmounting cancels the request instead
    // of letting it finish and throwing the answer away, and `signal.aborted`
    // is then the single thing to ask before touching state.
    const inFlight = new AbortController();
    void (async () => {
      try {
        const body = await get<Identity>("/auth/me", {
          signal: inFlight.signal,
        });
        if (inFlight.signal.aborted) return;
        setUser({ userId: body.user_id, email: body.email ?? undefined });
        noteSignedIn();
        setUnreachable(false);
      } catch (failure) {
        // Aborting is not an answer at all, so it leaves the state alone.
        if (inFlight.signal.aborted) return;
        setUser(null);
        // Only a 401 says this browser has no session: the ordinary answer
        // for a signed-out visitor. A 502, a 500 or no answer says nothing
        // about the session, so the attorney is not sent to sign in on its
        // strength; the page says the server was out of reach and asks again
        // when told to. Claiming a session instead would render an app whose
        // every call then fails.
        setUnreachable(
          !(failure instanceof ApiError && failure.status === 401),
        );
      } finally {
        if (!inFlight.signal.aborted) setIsLoading(false);
      }
    })();
    return () => {
      inFlight.abort();
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setIsLoading(true);
    setAttempt((n) => n + 1);
  }, []);

  return { user, isLoading, unreachable, retry };
}

function ApiSession({ children }: { children: ReactNode }) {
  const { user, isLoading, unreachable, retry } = useWhoIsSignedIn();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  // Who is signed in is asked on mount, and again on each Try again after the
  // server was out of reach. A session that ends after that answer is
  // reported by the API, one 401 at a time, and reaches here rather than
  // being handled at the route: a guard that redirects while this still says
  // "signed in" sends the browser to a sign-in page that sends it straight
  // back, forever.
  const expired = useSyncExternalStore(
    subscribeSessionExpired,
    isSessionExpired,
  );

  // Nothing clears the cache here, which is deliberate.
  //
  // Cached data belonging to one account must never be served to the next, and
  // it cannot be: signing in and signing out are both full-page navigations,
  // so the account on a given document never changes after the answer above.
  // Every sign-in therefore begins with an empty cache already, and signing
  // out empties it on the way past.
  //
  // Clearing it as the session arrives would be actively harmful. That answer
  // lands in the same commit that mounts the first authenticated components,
  // so it cancels the requests they have just issued and leaves them watching
  // a query that no longer exists, which shows as a screen that loads until the
  // tab is closed.

  const login = useCallback(
    (returnTo?: string) => {
      // Started, not completed: this hands the browser to the provider, and
      // whether anyone comes back signed in is not knowable from here. The
      // sign-in that succeeded is the identify that follows it.
      analytics.capture("sign_in_started");
      // Both refusals belong to the session being left behind. Held over, the
      // guard would bounce the browser back to sign-in the moment it returned.
      clearSessionExpired();
      clearAccessDenied();
      // A full navigation, not fetch: the browser has to follow the redirect
      // out to the provider, and fetch would follow it internally and land
      // nowhere.
      const target =
        returnTo ?? `${window.location.pathname}${window.location.search}`;
      window.location.href = `${AUTH_BASE}/login?returnTo=${encodeURIComponent(target)}`;
    },
    [analytics],
  );

  const logout = useCallback(() => {
    void (async () => {
      let logoutUrl: string | null = null;
      try {
        // Through the client, which attaches the CSRF header this write needs.
        logoutUrl = (await post<SignedOut>("/auth/logout")).logout_url;
      } catch {
        // An unanswered request and a refused one mean the same thing here,
        // and both are handled below.
      }

      if (logoutUrl === null) {
        // The session is still live: the request never arrived, or it was
        // refused before the route could revoke anything. Going to the
        // sign-in page anyway would look like signing out and be undone by
        // the very next page load, which finds the session, decides the user
        // is signed in, and lets them back in. Saying so is the only answer
        // that is true.
        toast.error("Could not sign you out. Please try again.");
        return;
      }

      // Reported only past the refusal above, so the event means a session
      // that actually ended rather than a button that was pressed.
      analytics.capture("signed_out");

      // Cleared only now, because both describe the session being ended and a
      // sign-out that failed has not ended one.
      clearAccessDenied();
      clearSessionExpired();
      queryClient.clear();
      window.location.href = logoutUrl;
    })();
  }, [analytics, queryClient]);

  return (
    <AuthValueContext
      value={{
        isAuthenticated: user !== null && !expired,
        sessionEnded: expired,
        isLoading,
        user: expired ? null : user,
        login,
        logout,
        unreachable: unreachable && !expired,
        retry,
      }}
    >
      {children}
    </AuthValueContext>
  );
}

/**
 * Presents an already-signed-in session, for automated browser checks.
 *
 * Selected at module load rather than per render, so the real session is never
 * consulted at all under a bypass.
 */
function BypassSession({ children }: { children: ReactNode }) {
  return (
    <AuthValueContext
      value={{
        isAuthenticated: true,
        sessionEnded: false,
        isLoading: false,
        user: { userId: BYPASS_USER.userId, email: BYPASS_USER.email },
        login: () => undefined,
        logout: () => undefined,
        unreachable: false,
        retry: () => undefined,
      }}
    >
      {children}
    </AuthValueContext>
  );
}

/**
 * Chosen once, at module load, from a constant the bundler resolves at build
 * time. A production build contains only the real branch.
 */
export const AuthProvider = AUTH_BYPASS ? BypassSession : ApiSession;

export function useAuth(): AuthContextValue {
  const value = use(AuthValueContext);
  if (!value) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return value;
}
